import { useNavigate } from '@tanstack/react-router';
import { ClientsListDTO } from '@modules/clients/entities/client/api/dto/clientsList.dto';
import { useTranslation } from 'react-i18next';
import { getCurrentShownRange } from '@components/Table';
import {
  Box,
  Flex, Pagination, Text
} from '@huspy/briks-web';
import { ClientsRoute, ClientDetailsRoute } from '@modules/clients/presentation/routes';
import Table from '@components/v2/Table';
import { EmptyTemplateComponent } from '@modules/core/components/EmptyTemplateComponent';

type Props = {
  data?: ClientsListDTO;
  isLoading?: boolean;
  error?: boolean;
};

const PAGE_SIZE = 7;

const EmptyClients = () => {
  const { t } = useTranslation();

  return (
    <Box w='100%'>
      <EmptyTemplateComponent
        title={ t('clients.empty.title') }
        description={ t('clients.empty.description') }
        cta={ null }
      />
    </Box>
  );
};

const ClientsTable = ({
  data = {
    clients: [],
    limit: 0,
    page: 0,
    total: 0,
  }, isLoading, error,
}: Props) => {
  const { sort, order, page } = ClientsRoute.useSearch();
  const navigate = useNavigate({ from: ClientsRoute.fullPath });
  const {
    clients, limit, page: currentPage, total,
  } = data;

  const currentlyShowingText = getCurrentShownRange(currentPage, limit, total);

  const { t } = useTranslation();

  const handleSortableHeadClick = (sortBy: string) => {
    if (sortBy === sort) {
      navigate({
        search: ({ order: _order, sort: _sort, ...prev }) => ({
          ...(_order === 'DESC' ? { ...prev } : {
            ...prev,
            sort: sortBy,
            order: order === 'ASC' ? 'DESC' : 'ASC',
          })
          ,
        }),
      });
    } else {
      navigate({
        search: (prev) => ({
          ...prev,
          sort: sortBy,
          order: 'ASC',
        }),
      });
    }
  };

  const handlePageSelect = (value: number) => {
    navigate({
      search: (prev) => ({
        ...prev,
        page: value,
      }),
    });
  };

  const rows = clients?.length > 0 ? clients.map((element) => (
    <Table.Row
      cursor='pointer'
      key={ element.id }
      data-test={ `client-row-${element.email}` }
      onClick={ () => {
        navigate({
          to: ClientDetailsRoute.to,
          params: { id: element.id },
        });
      } }
    >
      <Table.Cell style={ { width: '30%' } }>
        {`${element.first_name} ${element.last_name}`}
      </Table.Cell>
      <Table.Cell>
        {element.mobile}
      </Table.Cell>
      <Table.Cell>
        {element.email}
      </Table.Cell>
      <Table.Cell>
        {new Date(element.created_at).toLocaleDateString('en-GB')}
      </Table.Cell>
      <Table.Cell>
        {new Date(element.updated_at).toLocaleDateString('en-GB')}
      </Table.Cell>
    </Table.Row>
  )) : (null);

  if (clients?.length === 0 && !isLoading) {
    return <EmptyClients />;
  }

  return (
    <Flex gap={ 8 } direction='column'>
      <Table.Root>
        <Table.Head>
          <Table.Row>
            <Table.SortableHeader
              isSortedByThis={ sort === 'name' }
              order={ order }
              onClick={ () => handleSortableHeadClick('name') }
            >
              {t('clients.clientListTableHeaders.name') }
            </Table.SortableHeader>

            <Table.Header>
              {t('clients.clientListTableHeaders.phoneNumber') }
            </Table.Header>

            <Table.Header>
              {t('clients.clientListTableHeaders.email')}
            </Table.Header>

            <Table.Header>
              {t('clients.clientListTableHeaders.createdAt')}
            </Table.Header>

            <Table.SortableHeader
              isSortedByThis={ sort === 'last_updated' }
              order={ order }
              onClick={ () => handleSortableHeadClick('last_updated') }
            >
              {t('clients.clientListTableHeaders.lastUpdated')}
            </Table.SortableHeader>
          </Table.Row>
        </Table.Head>

        <Table.Body>{ isLoading && !error ? <Table.Loading rows={ PAGE_SIZE - 2 } cells={ 5 } /> : rows }</Table.Body>
      </Table.Root>

      <Flex align='center' justify='flex-end' gap={ 8 }>
        <Text size='md' color='text-tertiary'>
          {currentlyShowingText}
          {' '}
          { t('clients.of') }
          {' '}
          {total ?? 0}
          {' '}
          { t('clients.items') }
        </Text>

        <Pagination
          count={ total }
          pageSize={ limit }
          siblingCount={ 1 }
          defaultPage={ page }
          page={ page }
          onPageChange={ (change: { page: number, pageSize: number }) => { handlePageSelect(change.page); } }
        />
      </Flex>

    </Flex>
  );
};

export default ClientsTable;
